/**
 * Member list state machine - replaces useMemberStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type { CrmMember } from '../types'

export interface MemberListContext {
  members: CrmMember[]
  isLoading: boolean
}

export type MemberListEvent =
  | { type: 'SET_MEMBERS'; members: CrmMember[] }
  | { type: 'ADD_MEMBER'; member: CrmMember }
  | { type: 'UPDATE_MEMBER'; id: string; updates: Partial<CrmMember> }
  | { type: 'REMOVE_MEMBER'; id: string }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as MemberListContext,
    events: {} as MemberListEvent,
  },
})

export const memberListMachine = machineSetup.createMachine({
  id: 'memberList',
  context: {
    members: [],
    isLoading: false,
  },
  on: {
    SET_MEMBERS: {
      actions: assign({
        members: ({ event }) => (event.type === 'SET_MEMBERS' ? event.members : []),
      }),
    },
    ADD_MEMBER: {
      actions: assign({
        members: ({ context, event }) =>
          event.type === 'ADD_MEMBER' ? [...context.members, event.member] : context.members,
      }),
    },
    UPDATE_MEMBER: {
      actions: assign({
        members: ({ context, event }) => {
          if (event.type !== 'UPDATE_MEMBER') return context.members
          return context.members.map((m) =>
            m.id === event.id ? { ...m, ...event.updates } : m
          )
        },
      }),
    },
    REMOVE_MEMBER: {
      actions: assign({
        members: ({ context, event }) => {
          if (event.type !== 'REMOVE_MEMBER') return context.members
          return context.members.filter((m) => m.id !== event.id)
        },
      }),
    },
    SET_LOADING: {
      actions: assign({
        isLoading: ({ event }) => (event.type === 'SET_LOADING' ? event.loading : false),
      }),
    },
    CLEAR: {
      actions: assign({ members: [], isLoading: false }),
    },
  },
})
